import { fitFontSize, type MeasureFn, type Region } from "./layout";

/**
 * Layout of an AI resident's name tag (ADR-0010 decision 4: a human-looking resident whose name tag
 * carries a small "AI" badge). Same 512 x 128 texture and dark pill as every name tag, the name on
 * the left and a rounded cobalt "AI" pill where a visitor's country flag would be (residents have no
 * country). Pure, so the geometry is unit tested; signPainter.ts paints it.
 */
export const AI_BADGE = {
  text: "AI",
  /** Badge pill height on the 128 px texture. */
  height: 54,
  fontPx: 36,
  padX: 16,
  /** Gap between the name and the badge. */
  gap: 14,
  fill: "#2851D9",
  outline: "rgba(255,255,255,0.9)",
  ink: "#FFFFFF",
} as const;

export interface AiTagLayout {
  /** The dark rounded pill behind everything. */
  pill: Region;
  /** Name: left-aligned at x, vertically centred on y (textBaseline "middle"). */
  label: { x: number; y: number; fontPx: number; width: number };
  /** The "AI" badge pill and the centre of its text. */
  badge: Region & { textX: number; textY: number; fontPx: number };
}

export function layoutAiNameTag(measure: MeasureFn, label: string, width: number, height: number): AiTagLayout {
  const badgeTextW = measure(AI_BADGE.text, AI_BADGE.fontPx);
  const badgeW = Math.round(badgeTextW + AI_BADGE.padX * 2);
  const labelPx = fitFontSize(measure, label, width - 80 - badgeW - AI_BADGE.gap, height * 0.6, 58, 18);
  const labelW = measure(label, labelPx);
  const contentW = labelW + AI_BADGE.gap + badgeW;
  const pillW = Math.min(width - 8, contentW + 56);
  const pill: Region = { x: (width - pillW) / 2, y: 14, w: pillW, h: height - 28 };
  const x0 = Math.max(pill.x + 20, (width - contentW) / 2);
  const cy = height / 2 + 2;
  const bx = Math.round(Math.min(x0 + labelW + AI_BADGE.gap, pill.x + pill.w - 20 - badgeW));
  const by = Math.round(height / 2 - AI_BADGE.height / 2 + 1);
  return {
    pill,
    label: { x: x0, y: cy, fontPx: labelPx, width: labelW },
    badge: { x: bx, y: by, w: badgeW, h: AI_BADGE.height, textX: bx + badgeW / 2, textY: by + AI_BADGE.height / 2 + 1, fontPx: AI_BADGE.fontPx },
  };
}
